import * as THREE from 'three';

/**
 *
 * @category Background
 *
 * @class
 * @classdesc VideoSphere class is used to create a video background for your scene.
 * @extends THREE.Mesh
 * @returns {VideoSphere} A new VideoSphere THREE.Mesh instance
 *
 * @param {string} backgroundUrl - The url of the video file
 * @param {object} config - The configuration object
 * @param {boolean} config.autoplay - Autoplay the video
 * @param {boolean} config.loop - Loop the video
 * @param {boolean} config.muted - Mute the video
 * @param {function} onPlaying - Callback function when video is playing
 * @param {function} onError - Callback function when video.play() returns an error
 * @param {function} onVideoEnd - Callback function when video ends
 * @param {number} segments - The number of segments for the sphere geometry
 *
 * @name VideoSphere
 *
 * @example
 * const videoSphere = new VideoSphere(
 * 'https://cdn.obsess-vr.com/bravo/360_video.mp4',
 *  {
 *   autoplay: true,
 *   loop: true,
 *   muted: true,
 *  },
 *  () => console.log('playing'),
 *  () => console.log('error'),
 *  128,
 * );
 * scene.add(videoSphere);
 *
 *
 */
class VideoSphere extends THREE.Mesh {
	constructor(
		backgroundUrl,
		config = {},
		onPlaying,
		onError,
		segments = 128,
		onTimeUpdate = () => {},
		onVideoEnd = () => {},
	) {
		super(
			new THREE.SphereGeometry(20, segments, segments),
			new THREE.MeshBasicMaterial({ color: 0xffffff }),
		);

		this.config = config;
		this.geometry.scale(-1, 1, 1);
		this.renderOrder = 1;
		this.name = 'video360Background';
		this.onPlaying = onPlaying;
		this.onError = onError;
		this.onVideoEnd = onVideoEnd;
		this.backgroundUrl = backgroundUrl;
		this.videoEl = this.createVideoComponent(backgroundUrl, config);
		this.onTimeUpdate = onTimeUpdate;
		const texture = new THREE.VideoTexture(this.videoEl);
		// Required starting r152
		texture.colorSpace = THREE.SRGBColorSpace;
		this.material = new THREE.MeshBasicMaterial({
			map: texture,
			toneMapped: false,
		});
		this.videoEl.addEventListener('loadeddata', this.onLoadEvent);
		this.cues = null
	}

	/**
	 * @method
	 * @name VideoSphere#play
	 * @description Plays the video
	 * @returns {void}
	 */
	play = () => {
		this.videoEl
			.play()
			.then(() => {
				this.onPlaying(true);
			})
			.catch((e) => {
				this.onPlaying(true);
				console.error(e);
				if (e.name === 'NotAllowedError') {
					this.onError({
						code: 'VIDEO_PLAYBACK_ERROR',
						message:
							'Unable to play video due to browser restrictions.',
					});
				}
			});
	};

	pause = () => {
		this.videoEl.pause().then(() => {
			this.onPlaying(false);
		});
	};

	/**
	 * @method
	 * @name VideoSphere#_onVideoEnd
	 * @description Function run when the video ends
	 * @returns {void}
	 */
	_onVideoEnd = () => {
		const event = new CustomEvent('onSceneBackgroundVideoEnded');
		window.dispatchEvent(event);
		if (this.onVideoEnd) {
			this.onVideoEnd();
		}
	};

	/**
	 * @method
	 * @name VideoSphere#changeTrack
	 * @param {String} videoUrl - New URL for the video.
	 * @description Changes the source on the video element.
	 * @returns {void}
	 */
	changeTrack = (videoUrl) => {
		if (this.backgroundUrl === videoUrl) return;
		this.backgroundUrl = videoUrl;
		this.videoEl.src = videoUrl;
		this.videoEl.load();
	};

	/**
	 * @method
	 * @name VideoSphere#onLoadEvent
	 * @description Callback function when the video is loaded
	 * @returns {void}
	 */
	onLoadEvent = () => {
		if (this.config.autoplay) this.play();
		const caption = this.videoEl?.textTracks?.[0]
		if(caption) {
			caption.mode = "hidden"
			this.cues = caption.cues
		}
		this.videoEl.ontimeupdate = () => {
			this.onTimeUpdate(this.videoEl.currentTime);
		}
	};

	/**
	 * @method
	 * @name VideoSphere#createVideoComponent
	 * @description Creates a video element
	 * @param {String} videoUrl - The url of the video file
	 * @param {Object} config - The configuration object
	 * @param {Boolean} config.loop - Loop the video
	 * @param {Boolean} config.muted - Mute the video
	 * @returns {HTMLVideoElement} A new video element
	 */
	createVideoComponent = (videoUrl, config) => {
		const video = document.createElement('video');
		video.loop = config.loop;
		video.muted = config.muted;
		video.preload = 'auto';
		// video.autoplay = config.autoplay;
		video.playsInline = true;
		video.crossOrigin = 'anonymous';
		video.src = `${videoUrl}`;
		// add track
		if(config?.caption_file){
			const track = document.createElement('track');
			track.label = "English"
			track.srclang = "en-us"
			track.kind = "captions"
			track.default = ""
			track.src = config.caption_file
			video.appendChild(track)
		}
		video.load();
		video.addEventListener('ended', this._onVideoEnd);
		return video;
	};

	/**
	 * @method
	 * @name VideoSphere#setLoopConfig
	 * @description Sets the loop config
	 * @param {Boolean} value - The value to set
	 * @returns {void}
	 */
	setLoopConfig = (value) => {
		if (this.config.loop === value) return;
		this.videoEl.loop = value;
		this.config.loop = value;
		if (value === true && this.videoEl.paused) this.play();
	};

	/**
	 * @method
	 * @name VideoSphere#setMutedConfig
	 * @description Sets the muted config
	 * @param {Boolean} value - The value to set
	 * @returns {void}
	 */
	setMutedConfig = (value) => {
		this.videoEl.muted = value;
		this.config.muted = value;
	};

	/**
	 * @method
	 * @name VideoSphere#dispose
	 * @description Disposes the video component
	 * @returns {void}
	 *
	 */
	dispose = () => {
		this.geometry.dispose();
		this?.material?.map?.dispose();
		this.material.map = null;
		this.material.needsUpdate = true;
		this.material.dispose();
		this.videoEl.pause();
		this.videoEl.removeAttribute('src'); // empty source
		this.videoEl.load();
		this.videoEl.removeEventListener('canplay', this.onLoadEvent);
		this.videoEl.removeEventListener('playing', this.onPlaying);
		this.videoEl.removeEventListener('ended', this._onVideoEnd);
		this.videoEl = null;
		this.material = null;
	};
}

export default VideoSphere;
